import React, { FC, useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { IconBackground } from 'common/components';
import { EColor, EMode } from 'common/const';
import { useLegalEntityContext } from 'common/context';
import { LIST_LIMIT_0 } from 'common/config';
import { LegalEntity, Bin, Plus } from 'common/svg';
import { showMessage } from 'common/helpers';
import { WarningCard } from 'common/components/WarningCard';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { EditLegalEntityCard } from 'entities/LegalEntity/components';
import { ILegal } from 'entities/LegalEntity/LegalEntity.models';
import { deleteLegal, getLegalList, selectIsLegalLoading, selectLegalList, setLegalList } from 'entities/LegalEntity/Legal.slice';
import { getSetSelfRegistrationPreviousStep } from 'entities/SelfRegistration/SelfRegistration.slice';

export interface IComponentProps {
  onNext: () => void;
  loading: boolean;
  error: string | null;
}

export const ConnectedSelfRegistrationLegalEntitiesInner: FC<IComponentProps> = ({ onNext, loading, error }) => {
  const dispatch = useAppDispatch();

  const legalList = useAppSelector(selectLegalList);
  const isLegalLoading = useAppSelector(selectIsLegalLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [selectedLegalEntity, setSelectedLegalEntity] = useState<ILegal | null>(null);
  const { setEditLegalEntityCardMode, setOpenEditLegalEntityCard } = useLegalEntityContext();

  useEffect(() => {
    const fetch = async () => {
      dispatch(setLegalList([]));
      await dispatch(getLegalList({ limit: LIST_LIMIT_0 }));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  const onCreateClick = () => {
    setEditLegalEntityCardMode(EMode.Create);
    setOpenEditLegalEntityCard(true);
  };

  const onDeleteClick = (record: ILegal) => {
    setSelectedLegalEntity(record);
    setOpenWarningCard(true);
  };

  const onDeleteConfirm = () => {
    if (selectedLegalEntity) {
      dispatch(
        deleteLegal({
          id: selectedLegalEntity.id,
          onSuccess: () => {
            showMessage({
              content: `Юридическое лицо «${selectedLegalEntity.name}» удалено.`,
              icon: <Bin className="icon-bin-dark-grey" />,
            });
            setOpenWarningCard(false);
          },
        }),
      );
    }
  };

  const backward = (): void => {
    dispatch(getSetSelfRegistrationPreviousStep());
  };

  const hasLegalEntities = legalList.length > 0;

  return (
    <div className="self-registration-step">
      <div className="self-registration-step__title">Добавьте юридические лица</div>

      {error && <Alert type="error" message={error} showIcon />}

      <div className="mb-6">
        <Button className="button-sm primary" icon={<Plus className="icon-plus-white" />} onClick={onCreateClick}>
          Добавить юр. лицо
        </Button>
      </div>

      {fetchLoading ? null : !hasLegalEntities ? (
        <div className="self-registration-step__placeholder">Юридические лица ещё не добавлены.</div>
      ) : (
        <div className="self-registration-step__list">
          {legalList.map((entity) => (
            <div key={entity.id} className="self-registration-step__list-item">
              <div className="self-registration-step__list-item-info">
                <IconBackground content={<LegalEntity className="icon-legal-entity-bright-green" />} color={EColor.PaleGreen} />

                <div>
                  <div className="text-controls-accent mb-2">{entity.name}</div>
                  <span className="text-tag color-dark-grey">{entity.inn}</span>
                  <div className="text-tag color-dark-grey">{entity.isVatPayer ? 'Плательщик НДС' : 'Не плательщик НДС'}</div>
                </div>
              </div>

              <Button
                className="button-icon"
                icon={<Bin className="icon-bin-dark-grey" />}
                onClick={() => onDeleteClick(entity)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="self-registration-step__actions">
        <Button className="button-lg secondary" loading={loading} onClick={backward}>
          Назад
        </Button>
        <Button className="button-lg primary" loading={loading} onClick={onNext}>
          {hasLegalEntities ? 'Далее' : 'Пропустить'}
        </Button>
      </div>

      <EditLegalEntityCard />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите удалить юр. лицо?"
        subtitle="Юридическое лицо будет удалено."
        confirmBtnTitle="Удалить"
        loading={isLegalLoading}
        onConfirm={onDeleteConfirm}
        onClose={() => setOpenWarningCard(false)}
      />
    </div>
  );
};
